// The guided walkthrough (ported from Doug's Claude Design prototype,
// `Coronis Coding Audit.html` → src/help.jsx).
//
// ONE connected tour that crosses screens: batch list → inside a batch → case
// review → dashboard. Each step names the screen it needs via `nav`; the tour
// asks the host to navigate there before showing the step, and a request for the
// screen you are already on is a no-op.
//
// Copy is VERBATIM from the prototype. It is the product's own voice and it
// makes specific promises about behaviour ("auto-advance to the next case",
// "matching fields collapse automatically") — paraphrasing it would quietly
// change what the reviewer is told the tool does.

/** A screen the tour can ask the host to navigate to. */
export type TourScreen = "queue" | "batch" | "review" | "dashboard";

export interface TourStep {
  /** Screen this step needs. The host navigates there first. */
  nav: TourScreen;
  /**
   * CSS selector for the element to spotlight. Absent = a centred card with no
   * cutout (the welcome and any step whose target is off-screen).
   *
   * Selectors target `data-tour` attributes rather than class names or DOM
   * shape, so restyling a view cannot silently break the tour — a renamed class
   * would leave the step pointing at nothing, and the failure is invisible
   * (the step just stops highlighting) rather than an error.
   */
  selector?: string;
  title: string;
  body: string;
}

/**
 * The full tour, 12 steps.
 *
 * Kept as ONE array rather than per-screen tours: the point is the loop —
 * batches → cases → review → dashboard — and a reviewer who only ever sees the
 * screen they are standing on never learns where their verdict goes.
 */
export const FULL_TOUR: TourStep[] = [
  {
    nav: "queue",
    title: "Welcome to the Coding Audit workbench",
    body: "Twelve steps, start to finish: the batch list, a batch, a case review, and the dashboard where the results land. Replay it anytime from the Help menu.",
  },
  {
    nav: "queue",
    selector: '[data-tour="tour-batches"]',
    title: "Work arrives in batches",
    body: "A batch is one day at one facility for one client. Each card carries the client and job code, how many cases it holds, how many disagree, and who coded them.",
  },
  {
    nav: "queue",
    selector: '[data-tour="tour-search"]',
    title: "Find work fast",
    body: "Search matches the batch number, client, facility and day — and reaches inside batches to find a case, patient or MRN. Next we’ll open one.",
  },
  {
    nav: "batch",
    selector: '[data-tour="tour-caselist"]',
    title: "Inside a batch",
    body: "Every case for that day and facility. You can see who coded each one and how many fields the two results disagree on — that count is the quickest way to find the cases worth your attention first.",
  },
  {
    nav: "batch",
    selector: '[data-tour="tour-startreview"]',
    title: "Start reviewing",
    body: "This opens the first case you haven’t reviewed. Each time you submit you land on the next one, so you can work a whole batch without coming back here.",
  },
  {
    nav: "review",
    selector: '[data-tour="tour-fields"]',
    title: "Blind, field-by-field review",
    body: "Every case was coded twice, and you see both as A and B. Which is the AI and which is the human is withheld — from the screen and from the server behind it — so your judgement is about the coding, not the source. Fields collapse as you resolve them, leaving what’s still open.",
  },
  {
    nav: "review",
    selector: '[data-tour="tour-fieldcards"] > *:first-child',
    title: "Judge each field",
    body: "Mark whichever result is right. If neither is, choose “Neither” and search the codebook for the code that is. On fields carrying several codes, flag them one at a time — and add any the record supports that both results missed.",
  },
  {
    nav: "review",
    selector: '[data-tour="tour-qualifiers"]',
    title: "Anesthesia qualifiers",
    body: "Qualifiers like Emergency and unusual position are compared the same way. When the two disagree, set the value the record supports.",
  },
  {
    nav: "review",
    selector: '[data-tour="tour-submit"]',
    title: "Submit & keep moving",
    body: "Submit unlocks once every field is resolved. It records your verdicts and moves you straight to the next case.",
  },
  {
    nav: "dashboard",
    selector: '[data-tour="tour-period"]',
    title: "The audit dashboard",
    body: "Where your verdicts end up. These are the study’s headline numbers — how often the two sides agreed, and how often each one was right.",
  },
  {
    nav: "dashboard",
    selector: '[data-tour="tour-breakdown"]',
    title: "Drill into any field",
    body: "The per-field breakdown is where the signal is. A gap spread evenly across every field and a gap concentrated in one are different problems with different fixes.",
  },
  {
    nav: "dashboard",
    // No selector: the closing step is a summary of the whole loop, not a
    // pointer at one control. The prototype anchored it to a weekly-trend card
    // this dashboard does not have yet.
    title: "You’re all set",
    body: "That’s the loop: batches, cases, review, dashboard. The Help button brings this back anytime, along with the docs and a way to reach the team.",
  },
];

/** Links in the Help & guidance menu, below "Start the walkthrough". */
export interface HelpLink {
  label: string;
  desc: string;
  href: string;
}

export const HELP_LINKS: HelpLink[] = [
  { label: "Documentation", desc: "How the audit workbench works", href: "#" },
  { label: "FAQ", desc: "Common questions", href: "#" },
  { label: "Keyboard shortcuts", desc: "Work faster", href: "#" },
  {
    label: "Ask a question",
    desc: "Email the audit team",
    href: "mailto:audit-support@coronishealth.com",
  },
];
